import { Injectable, inject } from '@angular/core';
import { HttpClient } from '@angular/common/http';
import { environment } from '../environments/environment';
import {
  Assessment,
  Languages,
  Norms,
  JobProfiles,
  Package,
  Participant,
  Project,
  Report,
  SearchResult,
} from './models';
import { map, Observable } from 'rxjs';

@Injectable({ providedIn: 'root' })
export class ApiService {
  private http = inject(HttpClient);
  private base = environment.apiBaseUrl + '/api';
  private reportBase = environment.reportBaseUrl + '/api';

  // Assessments
  getAssessments(): Observable<Assessment[]> {
    return this.http.get<Assessment[]>(`${this.base}/assessments`);
  }

  // Participants
  getParticipants() {
    return this.http.get<Participant[]>(`${this.base}/participants`);
  }

  updateParticipant(participant: Participant) {
    return this.http.put<Participant>(`${this.base}/participants/${participant.id}`, participant);
  }

  getParticipantReports(participantId: string) {
    return this.http.get<Report[]>(`${this.base}/participants/${participantId}/reports`);
  }

  getParticipantPackages(participantId: string) {
    return this.http.get<Package[]>(`${this.base}/participants/${participantId}/packages`);
  }

  addParticipantReport(participantId: string, report: Report) {
    return this.http.post<void>(`${this.base}/participants/${participantId}/reports`, report);
  }

  deleteParticipantReport(participantId: string, reportId: string) {
    return this.http.delete<void>(`${this.base}/participants/${participantId}/reports/${reportId}`);
  }

  addParticipantPackage(participantId: string, pkg: Package) {
    return this.http.post<void>(`${this.base}/participants/${participantId}/packages`, pkg);
  }

  deleteParticipantPackage(participantId: string, packageId: string) {
    return this.http.delete<void>(
      `${this.base}/participants/${participantId}/packages/${packageId}`,
    );
  }

  updateParticipantPackage(participantId: string, pkg: Package) {
    return this.http.put<void>(
      `${this.base}/participants/${participantId}/packages/${pkg.id}`,
      pkg,
    );
  }

  updateParticipantReport(participantId: string, report: Report) {
    return this.http.put<void>(
      `${this.base}/participants/${participantId}/reports/${report.id}`,
      report,
    );
  }

  // Projects
  getProjects() {
    return this.http.get<Project[]>(`${this.base}/projects`);
  }

  getProjectReports(projectId: string) {
    return this.http.get<Report[]>(`${this.base}/projects/${projectId}/reports`);
  }

  getProjectReport(projectId: string, reportId: string) {
    return this.http.get<Report>(`${this.base}/projects/${projectId}/reports/${reportId}`);
  }
  addProjectReport(projectId: string, report: Report) {
    return this.http.post<void>(`${this.base}/projects/${projectId}/reports`, report);
  }

  updateProjectReport(projectId: string, report: Report) {
    return this.http.put<void>(`${this.base}/projects/${projectId}/reports/${report.id}`, report);
  }

  deleteProjectReport(projectId: string, reportId: string) {
    return this.http.delete<void>(`${this.base}/projects/${projectId}/reports/${reportId}`);
  }

  updateProjectParticipantReports(projectId: string, report: Report) {
    return this.http.put<void>(
      `${this.base}/projects/${projectId}/participants/reports/${report.id}?updateGenerated=False`,
      report,
    );
  }

  generateParticipantReport(
    clientId: string,
    projectId: string,
    participantId: string,
    reportId: string,
  ) {
    return this.http.post<void>(
      `${this.reportBase}/${clientId}/${projectId}/${participantId}/${reportId}/generate`,
      null,
    );
  }

  getReportLanguages(reportId: string) {
    return this.http.get<Languages>(`${this.base}/reports/${reportId}/languages`);
  }

  getReportNorms(reportId: string) {
    return this.http.get<Norms>(`${this.base}/reports/${reportId}/norms`);
  }

  getReportJobProfiles(reportId: string) {
    return this.http.get<JobProfiles>(`${this.base}/reports/${reportId}/job-profiles`);
  }

  getProjectPackages(projectId: string) {
    return this.http.get<Package[]>(`${this.base}/projects/${projectId}/packages`);
  }

  addProjectPackage(projectId: string, pkg: Package) {
    return this.http.post<void>(`${this.base}/projects/${projectId}/packages`, pkg);
  }

  updateProjectPackage(projectId: string, pkg: Package) {
    return this.http.put<void>(`${this.base}/projects/${projectId}/packages/${pkg.id}`, pkg);
  }

  deleteProjectPackage(projectId: string, packageId: string) {
    return this.http.delete<void>(`${this.base}/projects/${projectId}/packages/${packageId}`);
  }

  updateProjectParticipantPackages(projectId: string, pkg: Package) {
    return this.http.put<void>(
      `${this.base}/projects/${projectId}/participants/packages/${pkg.id}?updateGenerated=False`,
      pkg,
    );
  }

  getProject(projectId: string) {
    return this.http.get<Project>(`${this.base}/projects/${projectId}`);
  }

  getProjectParticipants(projectId: string) {
    return this.http.get<Participant[]>(`${this.base}/projects/${projectId}/participants`);
  }

  inviteProjectParticipants(projectId: string, emails: string[]) {
    return this.http.post<Participant[]>(`${this.base}/projects/${projectId}/participants`, emails);
  }

  getProjectAssessments(projectId: string) {
    return this.http.get<Assessment[]>(`${this.base}/projects/${projectId}/assessments`);
  }

  getProjectUnclaimedIds(projectId: string) {
    return this.http.get<string[]>(`${this.base}/projects/${projectId}/unclaimed-ids`);
  }

  getClientReports() {
    return this.http.get<Report[]>(`${this.base}/reports`);
  }

  getClientPackages() {
    return this.http.get<Package[]>(`${this.base}/packages`);
  }

  generateProjectIds(projectId: string, count: number) {
    return this.http.post<void>(`${this.base}/projects/${projectId}/batches`, count, {
      observe: 'response',
    });
  }

  downloadGeneratedIdsCsv(location: string) {
    let url = location;
    const activeClient = sessionStorage.getItem('activeClient');
    if (activeClient) {
      const separator = location.includes('?') ? '&' : '?';
      url = `${location}${separator}activeClient=${encodeURIComponent(activeClient)}`;
    }

    return this.http.get(url, { responseType: 'blob' });
  }

  downloadProjectUnclaimedIdsCsv(projectId: string) {
    return this.http.get(`${this.base}/projects/${projectId}/unclaimed-ids/csv`, {
      responseType: 'blob',
    });
  }

  downloadReports(options: DownloadOptions): Observable<DisposableUrl> {
    let path =
      'projectId' in options
        ? `${this.reportBase}/${options.clientId}/${options.projectId}/downloadallreports`
        : `${this.reportBase}/${options.clientId}/${options.participantId}/${options.reportId}/downloadreport`;

    return this.http
      .get(path, {
        responseType: 'blob',
      })
      .pipe(
        map((blob: Blob) => {
          const url = URL.createObjectURL(blob);
          return { url, revoke: () => URL.revokeObjectURL(url) };
        }),
      );
  }

  getParticipantReportStatus(clientId: string, participantId: string, reportId: string) {
    return this.http.get<number>(
      `${this.reportBase}/${clientId}/${participantId}/${reportId}/report/status`,
    );
  }

  getParticipantPackageStatus(clientId: string, participantId: string, packageId: string) {
    return this.http.get<number>(
      `${this.reportBase}/${clientId}/${participantId}/${packageId}/package/status`,
    );
  }

  search(query: string) {
    const encoded = encodeURIComponent(query);
    return this.http.get<SearchResult>(`${this.base}/searches/${encoded}`);
  }
}
export type DownloadOptions =
  | { clientId: string; projectId: string }
  | { clientId: string; participantId: string; reportId: string };

export type DisposableUrl = { url: string; revoke: () => void };
